import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, ValidatorFn } from '@angular/forms';

import { AppMetaDataItemNames, AssignmentEntity, InstructorEntity } from '../../../../meta-data/app-meta-data.service';
import {
  DwComponent, DwComponentType, DwExpressionService, DwFormContextMode, DwMdFormContextInfo, DwMetaDataFormApi, DwMetaDataFormStateService,
  DwMetaDataService, DwMetaDataServiceToken, DwSectionBaseComponent, DwUiConfigRegistryService, DwUIMetaDataConfig, DwUIMetaDataConfigToken,
  DwLookupSearchType, DwOrmDataService, DwOrmDataServiceToken, DwQueryHelper, DwQueryFilter, DwOperator, DwFilterType,
  deepCopy,
  DwEventService,
  DwGridEvents
}
  from '@devwareapps/devware-cap';
import {
  StudentAssignmentProgressEvent,
  studentAssignmentProgress
} from '../../models/student-assignment-progress-event.model';
import {
  AssignmentContentProgressFilterEvent,
  assignmentContentProgressFilter
} from '../../../shared/models/assignment-content-progress-filter-event.model';
import { CourseProgressVariableContext } from '../../../courses/models/course-progress-variable-context.model';
import { InstructorRepositoryService } from '../../../instructors/services/instructor-repository.service';
import { AssignmentStudentManagementService } from '../../services/assignment-student-management.service';

@DwComponent({
  key: 'assignmentUtil',
  name: 'Assignment Util',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.Assignment,
})
@Component({
  selector: 'app-assignment-util',
  templateUrl: './assignment-util.component.html',
  styleUrls: ['./assignment-util.component.scss']
})
export class AssignmentUtilComponent extends DwSectionBaseComponent implements OnDestroy {
  assignment: AssignmentEntity;

  originalAssignment: AssignmentEntity;
  formApi: DwMetaDataFormApi;
  instructor: InstructorEntity;
  private quickSearchTimeouts: number[] = [];
  private instructorValidationKey: string;
  private validatedInstructorControl: AbstractControl;
  private validatedLessonControl: AbstractControl;

  constructor(
    dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    @Inject(DwOrmDataServiceToken) private dataService: DwOrmDataService,
    private dwFormStateService: DwMetaDataFormStateService,
    private dwEventService: DwEventService,
    private instructorRepositoryService: InstructorRepositoryService,
    private assignmentStudentManagementService: AssignmentStudentManagementService
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.configureRequiredValidators();
    this.runAfterFormDataLoaded(() => this.configureRequiredValidators());
    this.setupEventHandlers();
    this.clearAssignmentStudentQuickSearch();

    // this.runAfterFormDataLoaded(() => {
    //   this.setupStudents();
    // });

    // this.formApi.getFormDataChanges(100).subscribe(() => {
    //   this.setupStudents();
    // });

    this.setupData();

  }

  ngOnDestroy(): void {
    this.cancelPendingQuickSearches();
    this.clearAssignmentStudentQuickSearch();
    super.ngOnDestroy();
  }


  setupData() {
    this.checkFields();

    this.subscriptions.push(
      this.formApi.getFormDataChanges<AssignmentEntity>().subscribe(assignment => {
        this.checkFields();
      })
    );

  }

  instructorInfoLoaded = false;

  checkFields() {
    this.configureRequiredValidators();
    this.assignment = this.formApi.getFormData<AssignmentEntity>();
    if (!this.assignment) {
      return;
    }

    this.validateInstructorSelection();

    if (!this.assignment.FlightSchoolId) {
      
      // if (this.instructor) {
      //   const partialAssignment : Partial<AssignmentEntity> = {
      //     FlightSchoolId: this.instructor.FlightSchoolId
      //   };
        
      //   this.formApi.patchFormData(partialAssignment);
      //   return;
      // }

      if (this.instructorInfoLoaded) {
        return;
      }

      this.instructorInfoLoaded = true;

      this.instructorRepositoryService.getCurrentInstructor()
        .subscribe((instructor) => {
          this.instructor = instructor;

          this.checkFields();

          // if (instructor?.FlightSchoolId) {
          //   this.assignment.FlightSchoolId = instructor.FlightSchoolId;

          //   this.formApi.patchFormData(this.assignment);
          // }
        });
    }
  }

  setupEventHandlers(): void {

    this.subscriptions.push(
      this.dwEventService.getEventData<StudentAssignmentProgressEvent>(studentAssignmentProgress, false).subscribe((event) => {
        this.viewProgressDetail(event);
      }));

    this.subscriptions.push(
      this.dwEventService.getEventData<AssignmentContentProgressFilterEvent>(assignmentContentProgressFilter, false).subscribe((event) => {
        this.viewProgressFilter(event);
      }));

    // Clear the quick search text when the tab is changed
    const variableContextSubscription = this.formApi.getVariableContext<CourseProgressVariableContext>()?.subscribe(context => {
      if (context.Tab_Index === 1) {
        this.clearAssignmentStudentQuickSearch();
      }
    });

    if (variableContextSubscription) {
      this.subscriptions.push(variableContextSubscription);
    }

    // If the assignment is updated, we need to refresh any assignment student grids
    this.subscriptions.push(
      this.dwEventService.getEventData(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.Assignment)).subscribe((event) => {
        this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.AssignmentStudent), {});
      })
    );

  }

  viewProgressDetail(event: StudentAssignmentProgressEvent) {
    if (!event.data?.StudentIdDisplay) {
      return;
    }

    if (event.data?.AssignmentId && Number(event.data.AssignmentId) !== this.resolveAssignmentId()) {
      return;
    }

    // Need to set selected selected lesson and the tab
    this.formApi?.patchVariableContext<CourseProgressVariableContext>({
      Tab_Index: 2
    });

    this.setAssignmentStudentQuickSearch(event.data?.StudentIdDisplay);
  }

  viewProgressFilter(event: AssignmentContentProgressFilterEvent) {
    if (!event?.searchText) {
      return;
    }

    if (event.assignmentId && Number(event.assignmentId) !== this.resolveAssignmentId()) {
      return;
    }

    this.formApi?.patchVariableContext<CourseProgressVariableContext>({
      Tab_Index: 2
    });

    this.setAssignmentStudentQuickSearch(event.searchText);
  }

  private setAssignmentStudentQuickSearch(searchText: string): void {
    this.cancelPendingQuickSearches();
    this.clearAssignmentStudentQuickSearch();

    this.queueAssignmentStudentQuickSearch(searchText, 0);
    this.queueAssignmentStudentQuickSearch(searchText, 150);
    this.queueAssignmentStudentQuickSearch(searchText, 400);
  }

  private queueAssignmentStudentQuickSearch(searchText: string, delayMs: number): void {
    const timeoutId = window.setTimeout(() => {
      this.dwEventService.publishEvent(DwGridEvents.setQuickSearchText,
        {
          itemName: AppMetaDataItemNames.AssignmentStudent,
          quickSearchText: searchText
        }
      );
    }, delayMs);

    this.quickSearchTimeouts.push(timeoutId);
  }

  private clearAssignmentStudentQuickSearch(): void {
    this.dwEventService.publishEvent(DwGridEvents.setQuickSearchText,
      {
        itemName: AppMetaDataItemNames.AssignmentStudent,
        quickSearchText: ''
      }
    );
  }

  private cancelPendingQuickSearches(): void {
    this.quickSearchTimeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
    this.quickSearchTimeouts = [];
  }

  private resolveAssignmentId(): number {
    const assignment = this.formApi?.getFormData<AssignmentEntity>();
    const assignmentId = assignment?.AssignmentId;

    return assignmentId ? Number(assignmentId) : null;
  }

  private configureRequiredValidators(): void {
    const instructorControl = this.formGroup.get('InstructorId');
    if (instructorControl && instructorControl !== this.validatedInstructorControl) {
      this.validatedInstructorControl = instructorControl;
      this.appendValidator(instructorControl, control =>
        Number(control.value) > 0 ? null : { required: true });
    }

    const lessonControl = this.formGroup.get('AssignmentCourseLesson');
    if (lessonControl && lessonControl !== this.validatedLessonControl) {
      this.validatedLessonControl = lessonControl;
      this.appendValidator(lessonControl, control => {
        const lessons = (control.value || []) as Array<{ CourseLessonId?: number; _isDeleted?: boolean }>;
        const hasSelectedLesson = lessons.some(lesson => lesson._isDeleted !== true && Number(lesson.CourseLessonId) > 0);

        return hasSelectedLesson ? null : { lessonCountError: { value: true } };
      });
    }
  }

  private appendValidator(control: AbstractControl, validator: ValidatorFn): void {
    if (!control) {
      return;
    }

    const existingValidator = control.validator;
    control.setValidators(existingValidator ? [existingValidator, validator] : [validator]);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private validateInstructorSelection(): void {
    const instructorControl = this.formGroup.get('InstructorId');
    const flightSchoolControl = this.formGroup.get('FlightSchoolId');
    const instructorId = Number(instructorControl?.value);
    const flightSchoolId = Number(flightSchoolControl?.value || this.assignment?.FlightSchoolId);

    if (!(instructorId > 0) || !(flightSchoolId > 0)) {
      this.instructorValidationKey = null;
      return;
    }

    const validationKey = `${flightSchoolId}:${instructorId}`;
    if (this.instructorValidationKey === validationKey) {
      return;
    }

    this.instructorValidationKey = validationKey;
    this.assignmentStudentManagementService.isInstructorAvailableForSchool(instructorId, flightSchoolId)
      .subscribe(isAvailable => {
        const currentInstructorId = Number(instructorControl.value);
        const currentFlightSchoolId = Number(flightSchoolControl?.value || this.assignment?.FlightSchoolId);
        if (`${currentFlightSchoolId}:${currentInstructorId}` !== validationKey || isAvailable) {
          return;
        }

        instructorControl.setValue(null);
        instructorControl.markAsTouched();
        instructorControl.updateValueAndValidity();
      }, () => {
        this.instructorValidationKey = null;
      });
  }


}
